/**
 * `contracts/client-api.md` § The permission mirror: fed by the 403 `forbidden` responses,
 * keyed by the operation and the project or workspace it was attempted in, used only to hide
 * and disable controls and never as an authorisation decision. Feature 010 primes it from
 * `GET /workspaces/{ws}/effective-permissions` through `prime`.
 */
export interface PermissionScope {
  readonly workspace: string;
  readonly project?: string;
}

export interface PermissionMirror {
  /** Record a refusal of `operation` in `scope`. */
  recordRefusal(operation: string, scope: PermissionScope): void;
  /** Prime the mirror with the keys the caller holds; every other key in the scope is a refusal. */
  prime(scope: PermissionScope, held: readonly string[]): void;
  /** True when the mirror has seen `operation` refused in `scope` or in its workspace. */
  isRefused(operation: string, scope: PermissionScope): boolean;
  /** Forget a scope, on a membership change event or a re-authentication. */
  clear(scope?: PermissionScope): void;
  subscribe(listener: () => void): () => void;
}

function scopeKey(scope: PermissionScope): string {
  return scope.project === undefined ? `ws:${scope.workspace}` : `ws:${scope.workspace}/project:${scope.project}`;
}

export function createPermissionMirror(): PermissionMirror {
  const refused = new Map<string, Set<string>>();
  const primed = new Map<string, Set<string>>();
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  return {
    recordRefusal(operation, scope) {
      const key = scopeKey(scope);
      const set = refused.get(key) ?? new Set<string>();
      set.add(operation);
      refused.set(key, set);
      notify();
    },
    prime(scope, held) {
      primed.set(scopeKey(scope), new Set(held));
      refused.delete(scopeKey(scope));
      notify();
    },
    isRefused(operation, scope) {
      const own = scopeKey(scope);
      const workspace = scopeKey({ workspace: scope.workspace });
      if (refused.get(own)?.has(operation) === true) return true;
      if (scope.project !== undefined && refused.get(workspace)?.has(operation) === true) return true;
      const held = primed.get(own) ?? (scope.project === undefined ? undefined : primed.get(workspace));
      return held !== undefined && !held.has(operation);
    },
    clear(scope) {
      if (scope === undefined) {
        refused.clear();
        primed.clear();
      } else {
        refused.delete(scopeKey(scope));
        primed.delete(scopeKey(scope));
      }
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}
